// frontend/src/utils/index.js - ALL UTILITIES CONSOLIDATED IN ONE FILE

// =============================================
// CONSTANTS
// =============================================

// API Endpoints
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Resolve a stored image reference to a loadable URL. Handles relative
// "/uploads/…" paths, legacy absolute URLs saved while running locally
// ("http://localhost:5000/uploads/…"), and external URLs. Returns null for
// values that aren't paths at all (e.g. emoji placeholders like "🍰").
export const resolveUploadUrl = (value) => {
  if (!value || typeof value !== 'string') return null;
  const origin = API_BASE_URL.replace(/\/api\/?$/, '');
  const legacy = value.match(/^https?:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?(\/uploads\/.*)$/i);
  if (legacy) return `${origin}${legacy[1]}`;
  if (/^(https?:|data:|blob:)/i.test(value)) return value;
  if (value.startsWith('/')) return `${origin}${value}`;
  if (value.startsWith('uploads/')) return `${origin}/${value}`;
  return null;
};

export const API_ENDPOINTS = {
  // Auth endpoints
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    VERIFY: '/auth/verify',
    RESET_PASSWORD: '/auth/reset-password',
    CHANGE_PASSWORD: '/auth/change-password',
  },
  
  // User endpoints
  USER: {
    PROFILE: '/user/profile',
    UPDATE_PROFILE: '/user/profile/update',
    PREFERENCES: '/user/preferences',
    BOOKINGS: '/user/bookings',
    FAVORITES: '/user/favorites',
  },
  
  // Reservation endpoints
  RESERVATIONS: {
    CREATE: '/reservations/create',
    LIST: '/reservations',
    DETAIL: '/reservations/:id',
    UPDATE: '/reservations/:id/update',
    CANCEL: '/reservations/:id/cancel',
    CHECK_AVAILABILITY: '/reservations/check-availability',
  },
  
  // Room endpoints
  ROOMS: {
    LIST: '/rooms',
    DETAIL: '/rooms/:id',
    SEARCH: '/rooms/search',
    CATEGORIES: '/rooms/categories',
    AMENITIES: '/rooms/amenities',
  },
  
  // Payment endpoints
  PAYMENTS: {
    PROCESS: '/payments/process',
    VERIFY: '/payments/verify',
    HISTORY: '/payments/history',
    METHODS: '/payments/methods',
  },
  
  // Admin endpoints
  ADMIN: {
    DASHBOARD: '/admin/dashboard',
    USERS: '/admin/users',
    RESERVATIONS: '/admin/reservations',
    ROOMS: '/admin/rooms',
    REPORTS: '/admin/reports',
    SETTINGS: '/admin/settings',
  }
};

// Room Types
export const ROOM_TYPES = {
  STANDARD: 'standard',
  DELUXE: 'deluxe',
  SUITE: 'suite',
  EXECUTIVE: 'executive',
  PRESIDENTIAL: 'presidential',
  VILLA: 'villa',
};

// Room Type Labels
export const ROOM_TYPE_LABELS = {
  [ROOM_TYPES.STANDARD]: 'Standard Room',
  [ROOM_TYPES.DELUXE]: 'Deluxe Room',
  [ROOM_TYPES.SUITE]: 'Suite',
  [ROOM_TYPES.EXECUTIVE]: 'Executive Suite',
  [ROOM_TYPES.PRESIDENTIAL]: 'Presidential Suite',
  [ROOM_TYPES.VILLA]: 'Villa',
};

// Reservation Status
export const RESERVATION_STATUS = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  CHECKED_IN: 'checked_in',
  CHECKED_OUT: 'checked_out',
  CANCELLED: 'cancelled',
  NO_SHOW: 'no_show',
};

// Reservation Status Labels
export const RESERVATION_STATUS_LABELS = {
  [RESERVATION_STATUS.PENDING]: 'Pending',
  [RESERVATION_STATUS.CONFIRMED]: 'Confirmed',
  [RESERVATION_STATUS.CHECKED_IN]: 'Checked In',
  [RESERVATION_STATUS.CHECKED_OUT]: 'Checked Out',
  [RESERVATION_STATUS.CANCELLED]: 'Cancelled',
  [RESERVATION_STATUS.NO_SHOW]: 'No Show',
};

// Payment Status
export const PAYMENT_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  REFUNDED: 'refunded',
  PARTIALLY_REFUNDED: 'partially_refunded',
};

// Payment Methods
export const PAYMENT_METHODS = {
  CREDIT_CARD: 'credit_card',
  DEBIT_CARD: 'debit_card',
  PAYPAL: 'paypal',
  BANK_TRANSFER: 'bank_transfer',
  CASH: 'cash',
  CRYPTO: 'crypto',
};

// User Roles
export const USER_ROLES = {
  GUEST: 'guest',
  CUSTOMER: 'customer',
  STAFF: 'staff',
  MANAGER: 'manager',
  ADMIN: 'admin',
  SUPER_ADMIN: 'super_admin',
};

// Date Formats
export const DATE_FORMATS = {
  DISPLAY: 'MMM DD, YYYY',
  DISPLAY_WITH_TIME: 'MMM DD, YYYY HH:mm',
  INPUT: 'YYYY-MM-DD',
  API: 'YYYY-MM-DDTHH:mm:ss.SSSZ',
  SHORT: 'MM/DD/YY',
  LONG: 'MMMM DD, YYYY',
  TIME_ONLY: 'HH:mm',
  TIME_12H: 'hh:mm A',
};

// Currency
// IMPORTANT: The runtime currency is driven by the public Settings document
// (payment.currency, with menu.currency_symbol as a legacy fallback) and is
// exposed throughout the app via AppContext (`useApp().formatCurrency`).
// These constants are last-resort defaults only — they apply before public
// settings finish loading or in pure helper contexts that don't have access
// to React state. Do NOT hardcode currency strings anywhere else; always
// route money formatting through `useApp().formatCurrency` (or
// `formatCurrencyFromSettings` below).
export const CURRENCY = {
  DEFAULT: 'PKR',
  DECIMAL_PLACES: 0,
  // Legacy alias retained for back-compat with older imports.
  SYMBOL: 'PKR',
};

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 10,
  LIMITS: [10, 25, 50, 100],
};

// Validation Rules
export const VALIDATION_RULES = {
  EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE_REGEX: /^[\+]?[1-9][\d]{0,15}$/,
  PASSWORD_REGEX: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
  PASSWORD_MIN_LENGTH: 8,
  PASSWORD_MAX_LENGTH: 128,
  NAME_MIN_LENGTH: 2,
  NAME_MAX_LENGTH: 100,
  NAME_REGEX: /^[a-zA-Z\s\-'\.]+$/,
  USERNAME_REGEX: /^[a-zA-Z0-9_-]{3,30}$/,
  SLUG_REGEX: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
  HEX_COLOR_REGEX: /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/,
  URL_REGEX: /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/,
  IPV4_REGEX: /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
  IPV6_REGEX: /^(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))$/,
  POSTAL_CODE_REGEX: /^[0-9A-Za-z\s-]{3,10}$/,
  CREDIT_CARD_REGEX: /^[0-9]{13,19}$/,
  CVV_REGEX: /^[0-9]{3,4}$/,
  EXPIRY_MONTH_REGEX: /^(0[1-9]|1[0-2])$/,
  EXPIRY_YEAR_REGEX: /^[0-9]{2,4}$/,
  PAKISTANI_PHONE_REGEX: /^(\+92|0)?[0-9]{10}$/,
  PAKISTANI_CNIC_REGEX: /^[0-9]{5}-[0-9]{7}-[0-9]$/,
};

// Error Messages
export const ERROR_MESSAGES = {
  GENERIC: 'Something went wrong. Please try again.',
  NETWORK: 'Network error. Please check your connection.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  FORBIDDEN: 'Access forbidden.',
  NOT_FOUND: 'The requested resource was not found.',
  VALIDATION: 'Please check your input and try again.',
  SESSION_EXPIRED: 'Your session has expired. Please login again.',
  
  // Field specific
  REQUIRED_FIELD: 'This field is required',
  INVALID_EMAIL: 'Please enter a valid email address',
  INVALID_PHONE: 'Please enter a valid phone number',
  INVALID_PASSWORD: 'Password must contain at least one uppercase letter, lowercase letter, number, and special character',
  PASSWORDS_NOT_MATCH: 'Passwords do not match',
  INVALID_DATE: 'Please enter a valid date',
  DATE_IN_PAST: 'Date cannot be in the past',
  CHECKOUT_BEFORE_CHECKIN: 'Check-out date must be after check-in date',
  INVALID_NAME: 'Name contains invalid characters',
  INVALID_USERNAME: 'Username can only contain letters, numbers, hyphens, and underscores',
  INVALID_URL: 'Please enter a valid URL',
  INVALID_COLOR: 'Please enter a valid color code',
  INVALID_FILE_TYPE: 'Invalid file type',
  FILE_TOO_LARGE: 'File size is too large',
  INVALID_CREDIT_CARD: 'Please enter a valid credit card number',
  INVALID_CVV: 'Please enter a valid CVV',
  INVALID_EXPIRY: 'Please enter a valid expiry date',
  INVALID_POSTAL_CODE: 'Please enter a valid postal code',
  INVALID_IP: 'Please enter a valid IP address',
  INVALID_CNIC: 'Please enter a valid CNIC number',
  TOO_SHORT: 'This field is too short',
  TOO_LONG: 'This field is too long',
  INVALID_RANGE: 'Value is out of acceptable range',
  INVALID_FORMAT: 'Invalid format',
};

// Success Messages
export const SUCCESS_MESSAGES = {
  LOGIN: 'Welcome back! You have successfully logged in.',
  REGISTER: 'Registration successful! Welcome to SWAT Capital Resort.',
  LOGOUT: 'You have been logged out successfully.',
  PROFILE_UPDATE: 'Your profile has been updated successfully.',
  RESERVATION_CREATE: 'Your reservation has been confirmed!',
  RESERVATION_UPDATE: 'Your reservation has been updated successfully.',
  RESERVATION_CANCEL: 'Your reservation has been cancelled.',
  PASSWORD_RESET: 'Password reset link has been sent to your email.',
  PASSWORD_CHANGE: 'Your password has been changed successfully.',
  PAYMENT_SUCCESS: 'Payment processed successfully!',
};

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'swat_auth_token',
  REFRESH_TOKEN: 'swat_refresh_token',
  USER_DATA: 'swat_user_data',
  CART: 'swat_cart',
  PREFERENCES: 'swat_preferences',
  RECENT_SEARCHES: 'swat_recent_searches',
  FAVORITES: 'swat_favorites',
  LANGUAGE: 'swat_language',
  THEME: 'swat_theme',
};

// Theme
export const THEME = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
};

// Languages
export const LANGUAGES = {
  EN: { code: 'en', name: 'English', flag: '🇺🇸' },
  ES: { code: 'es', name: 'Español', flag: '🇪🇸' },
  FR: { code: 'fr', name: 'Français', flag: '🇫🇷' },
  DE: { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  IT: { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  PT: { code: 'pt', name: 'Português', flag: '🇵🇹' },
  AR: { code: 'ar', name: 'العربية', flag: '🇸🇦', rtl: true },
};

// Social Media Links
export const SOCIAL_LINKS = {
  FACEBOOK: 'https://facebook.com/swatcapitalresort',
  TWITTER: 'https://twitter.com/swatcapitalresort',
  INSTAGRAM: 'https://instagram.com/swatcapitalresort',
  LINKEDIN: 'https://linkedin.com/company/swatcapitalresort',
  YOUTUBE: 'https://youtube.com/swatcapitalresort',
};

// Contact Information
export const CONTACT_INFO = {
  PHONE: '+92 332 9622132',
  EMAIL: 'swatcapitalresort@gmail.com',
  ADDRESS: 'Swat Valley, Khyber Pakhtunkhwa, Pakistan',
  SUPPORT_EMAIL: 'support@swatcapitalresort.com',
  RESERVATIONS_EMAIL: 'reservations@swatcapitalresort.com',
};

// Map Configuration
export const MAP_CONFIG = {
  DEFAULT_CENTER: { lat: 35.2227, lng: 72.4258 }, // Swat Valley coordinates
  DEFAULT_ZOOM: 15,
  API_KEY: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
};

// File Upload Limits
export const FILE_UPLOAD = {
  MAX_SIZE: 5 * 1024 * 1024, // 5MB
  ALLOWED_TYPES: {
    IMAGES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    DOCUMENTS: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  },
  MAX_FILES: 5,
};

// Animation Durations (in ms)
export const ANIMATION_DURATION = {
  FAST: 150,
  NORMAL: 300,
  SLOW: 500,
  VERY_SLOW: 1000,
};

// Breakpoints
export const BREAKPOINTS = {
  XS: 0,
  SM: 640,
  MD: 768,
  LG: 1024,
  XL: 1280,
  XXL: 1536,
};

// Z-Index Levels
export const Z_INDEX = {
  DROPDOWN: 1000,
  STICKY: 1020,
  FIXED: 1030,
  MODAL_BACKDROP: 1040,
  MODAL: 1050,
  POPOVER: 1060,
  TOOLTIP: 1070,
  NOTIFICATION: 1080,
};

// =============================================
// VALIDATORS
// =============================================

/**
 * Validate required field
 * @param {any} value - Value to validate
 * @param {string} fieldName - Field name for error message
 * @returns {object} Validation result
 */
export const validateRequired = (value, fieldName = 'Field') => {
  const isValid = value !== null && value !== undefined && value !== '' && 
                  (typeof value !== 'string' || value.trim().length > 0) &&
                  (!Array.isArray(value) || value.length > 0);
  
  return {
    isValid,
    error: isValid ? null : `${fieldName} is required`
  };
};

/**
 * Validate email with enhanced pattern checking
 * @param {string} email - Email to validate
 * @returns {object} Validation result
 */
export const validateEmail = (email) => {
  if (!email) {
    return { isValid: false, error: ERROR_MESSAGES.REQUIRED_FIELD };
  }
  
  const trimmedEmail = email.trim().toLowerCase();
  
  // Basic format validation
  if (!VALIDATION_RULES.EMAIL_REGEX.test(trimmedEmail)) {
    return { isValid: false, error: ERROR_MESSAGES.INVALID_EMAIL };
  }
  
  // Check for common email issues
  if (trimmedEmail.includes('..') || trimmedEmail.startsWith('.') || trimmedEmail.endsWith('.')) {
    return { isValid: false, error: ERROR_MESSAGES.INVALID_EMAIL };
  }
  
  // Check length
  if (trimmedEmail.length > 254) {
    return { isValid: false, error: 'Email address is too long' };
  }
  
  return { isValid: true, error: null };
};

/**
 * Validate phone number with multiple format support
 * @param {string} phone - Phone number to validate
 * @param {object} options - Validation options
 * @returns {object} Validation result
 */
export const validatePhone = (phone, options = {}) => {
  if (!phone) {
    return { isValid: false, error: ERROR_MESSAGES.REQUIRED_FIELD };
  }
  
  const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
  
  // Check if it's a Pakistani number
  if (options.country === 'PK' || options.pakistaniOnly) {
    const isValid = VALIDATION_RULES.PAKISTANI_PHONE_REGEX.test(cleanPhone);
    return {
      isValid,
      error: isValid ? null : 'Please enter a valid Pakistani phone number'
    };
  }
  
  // General international phone validation
  const isValid = VALIDATION_RULES.PHONE_REGEX.test(cleanPhone) && cleanPhone.length >= 10 && cleanPhone.length <= 15;
  
  return {
    isValid,
    error: isValid ? null : ERROR_MESSAGES.INVALID_PHONE
  };
};

/**
 * Enhanced password validation with strength checking
 * @param {string} password - Password to validate
 * @param {object} options - Validation options
 * @returns {object} Validation result with strength score
 */
export const validatePassword = (password, options = {}) => {
  if (!password) {
    return { isValid: false, error: ERROR_MESSAGES.REQUIRED_FIELD, strength: 0 };
  }
  
  const minLength = options.minLength || VALIDATION_RULES.PASSWORD_MIN_LENGTH;
  const maxLength = options.maxLength || VALIDATION_RULES.PASSWORD_MAX_LENGTH;
  
  // Length check
  if (password.length < minLength) {
    return {
      isValid: false,
      error: `Password must be at least ${minLength} characters long`,
      strength: 0
    };
  }
  
  if (password.length > maxLength) {
    return {
      isValid: false,
      error: `Password cannot exceed ${maxLength} characters`,
      strength: 0
    };
  }
  
  // Calculate strength score
  let strength = 0;
  const checks = [
    { regex: /[a-z]/, points: 1 }, // lowercase
    { regex: /[A-Z]/, points: 1 }, // uppercase
    { regex: /[0-9]/, points: 1 }, // numbers
    { regex: /[@$!%*?&]/, points: 1 }, // special chars
    { test: () => password.length >= 12, points: 1 }, // length bonus
  ];
  
  checks.forEach(check => {
    if (check.regex ? check.regex.test(password) : check.test()) {
      strength += check.points;
    }
  });
  
  // Basic requirements check
  const hasRequired = VALIDATION_RULES.PASSWORD_REGEX.test(password);
  
  if (options.requireStrong && strength < 4) {
    return {
      isValid: false,
      error: 'Password is not strong enough. Include uppercase, lowercase, numbers, and special characters.',
      strength
    };
  }
  
  return {
    isValid: hasRequired,
    error: hasRequired ? null : ERROR_MESSAGES.INVALID_PASSWORD,
    strength
  };
};

/**
 * Get password strength level
 * @param {number} strength - Strength score (0-5)
 * @returns {object} Strength level info
 */
export const getPasswordStrength = (strength) => {
  const levels = [
    { level: 'Very Weak', color: 'red', score: 0 },
    { level: 'Weak', color: 'orange', score: 1 },
    { level: 'Fair', color: 'yellow', score: 2 },
    { level: 'Good', color: 'blue', score: 3 },
    { level: 'Strong', color: 'green', score: 4 },
    { level: 'Very Strong', color: 'green', score: 5 },
  ];
  
  return levels.find(l => l.score === strength) || levels[0];
};

/**
 * Validate password confirmation
 * @param {string} password - Original password
 * @param {string} confirmPassword - Confirmation password
 * @returns {object} Validation result
 */
export const validatePasswordConfirmation = (password, confirmPassword) => {
  if (!confirmPassword) {
    return { isValid: false, error: 'Please confirm your password' };
  }
  
  const isValid = password === confirmPassword;
  
  return {
    isValid,
    error: isValid ? null : ERROR_MESSAGES.PASSWORDS_NOT_MATCH
  };
};

/**
 * Enhanced name validation
 * @param {string} name - Name to validate
 * @param {object} options - Validation options
 * @returns {object} Validation result
 */
export const validateName = (name, options = {}) => {
  if (!name) {
    return { isValid: false, error: ERROR_MESSAGES.REQUIRED_FIELD };
  }
  
  const trimmedName = name.trim();
  const minLength = options.minLength || VALIDATION_RULES.NAME_MIN_LENGTH;
  const maxLength = options.maxLength || VALIDATION_RULES.NAME_MAX_LENGTH;
  
  if (trimmedName.length < minLength) {
    return {
      isValid: false,
      error: `Name must be at least ${minLength} characters long`
    };
  }
  
  if (trimmedName.length > maxLength) {
    return {
      isValid: false,
      error: `Name cannot exceed ${maxLength} characters`
    };
  }
  
  // Check for valid characters
  const isValid = VALIDATION_RULES.NAME_REGEX.test(trimmedName);
  
  return {
    isValid,
    error: isValid ? null : ERROR_MESSAGES.INVALID_NAME
  };
};

/**
 * Validate username
 * @param {string} username - Username to validate
 * @returns {object} Validation result
 */
export const validateUsername = (username) => {
  if (!username) {
    return { isValid: false, error: ERROR_MESSAGES.REQUIRED_FIELD };
  }
  
  const isValid = VALIDATION_RULES.USERNAME_REGEX.test(username.trim());
  
  return {
    isValid,
    error: isValid ? null : ERROR_MESSAGES.INVALID_USERNAME
  };
};

/**
 * Validate CNIC (Pakistani National ID)
 * @param {string} cnic - CNIC to validate
 * @returns {object} Validation result
 */
export const validateCNIC = (cnic) => {
  if (!cnic) {
    return { isValid: false, error: ERROR_MESSAGES.REQUIRED_FIELD };
  }
  
  const cleanCNIC = cnic.replace(/\s/g, '');
  const isValid = VALIDATION_RULES.PAKISTANI_CNIC_REGEX.test(cleanCNIC);
  
  return {
    isValid,
    error: isValid ? null : ERROR_MESSAGES.INVALID_CNIC
  };
};

/**
 * Enhanced date validation
 * @param {Date|string} date - Date to validate
 * @param {object} options - Validation options
 * @returns {object} Validation result
 */
export const validateDate = (date, options = {}) => {
  if (!date) {
    return { isValid: false, error: ERROR_MESSAGES.REQUIRED_FIELD };
  }
  
  const dateObj = date instanceof Date ? date : new Date(date);
  
  if (isNaN(dateObj.getTime())) {
    return { isValid: false, error: ERROR_MESSAGES.INVALID_DATE };
  }
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Check if date should be in future
  if (options.futureOnly) {
    if (dateObj < today) {
      return { isValid: false, error: ERROR_MESSAGES.DATE_IN_PAST };
    }
  }
  
  // Check if date should be in past
  if (options.pastOnly) {
    if (dateObj > today) {
      return { isValid: false, error: 'Date cannot be in the future' };
    }
  }
  
  // Check min date
  if (options.minDate) {
    const minDate = options.minDate instanceof Date ? options.minDate : new Date(options.minDate);
    if (dateObj < minDate) {
      return { isValid: false, error: `Date must be after ${minDate.toLocaleDateString()}` };
    }
  }
  
  // Check max date
  if (options.maxDate) {
    const maxDate = options.maxDate instanceof Date ? options.maxDate : new Date(options.maxDate);
    if (dateObj > maxDate) {
      return { isValid: false, error: `Date must be before ${maxDate.toLocaleDateString()}` };
    }
  }
  
  // Check age restrictions
  if (options.minAge) {
    const ageLimitDate = new Date();
    ageLimitDate.setFullYear(ageLimitDate.getFullYear() - options.minAge);
    if (dateObj > ageLimitDate) {
      return { isValid: false, error: `Must be at least ${options.minAge} years old` };
    }
  }
  
  return { isValid: true, error: null };
};

/**
 * Validate date range
 * @param {Date|string} startDate - Start date
 * @param {Date|string} endDate - End date
 * @param {object} options - Validation options
 * @returns {object} Validation result
 */
export const validateDateRange = (startDate, endDate, options = {}) => {
  // Validate individual dates
  const startValidation = validateDate(startDate, { futureOnly: options.futureOnly });
  if (!startValidation.isValid) {
    return { isValid: false, error: `Start date: ${startValidation.error}` };
  }
  
  const endValidation = validateDate(endDate, { futureOnly: options.futureOnly });
  if (!endValidation.isValid) {
    return { isValid: false, error: `End date: ${endValidation.error}` };
  }
  
  // Compare dates
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);
  
  if (end <= start) {
    return { isValid: false, error: 'End date must be after start date' };
  }
  
  // Check minimum duration
  if (options.minDuration) {
    const diffMs = end - start;
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    if (diffDays < options.minDuration) {
      return { 
        isValid: false, 
        error: `Duration must be at least ${options.minDuration} day${options.minDuration > 1 ? 's' : ''}` 
      };
    }
  }
  
  // Check maximum duration
  if (options.maxDuration) {
    const diffMs = end - start;
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    if (diffDays > options.maxDuration) {
      return { 
        isValid: false, 
        error: `Duration cannot exceed ${options.maxDuration} day${options.maxDuration > 1 ? 's' : ''}` 
      };
    }
  }
  
  return { isValid: true, error: null };
};

/**
 * Enhanced credit card validation with type detection
 * @param {string} cardNumber - Card number to validate
 * @returns {object} Validation result with card type
 */
export const validateCreditCard = (cardNumber) => {
  if (!cardNumber) {
    return { isValid: false, error: ERROR_MESSAGES.REQUIRED_FIELD };
  }
  
  // Remove spaces and non-numeric characters
  const cleaned = cardNumber.replace(/\D/g, '');
  
  // Check length
  if (cleaned.length < 13 || cleaned.length > 19) {
    return { isValid: false, error: 'Invalid card number length' };
  }
  
  // Detect card type
  const cardTypes = {
    visa: /^4[0-9]{12}(?:[0-9]{3})?$/,
    mastercard: /^5[1-5][0-9]{14}$/,
    amex: /^3[47][0-9]{13}$/,
    discover: /^6(?:011|5[0-9]{2})[0-9]{12}$/,
    dinersclub: /^3[0689][0-9]{11}$/,
    jcb: /^(?:2131|1800|35\d{3})\d{11}$/,
  };
  
  let cardType = 'unknown';
  for (const [type, pattern] of Object.entries(cardTypes)) {
    if (pattern.test(cleaned)) {
      cardType = type;
      break;
    }
  }
  
  // Luhn algorithm validation
  let sum = 0;
  let isEven = false;
  
  for (let i = cleaned.length - 1; i >= 0; i--) {
    let digit = parseInt(cleaned.charAt(i), 10);
    
    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }
    
    sum += digit;
    isEven = !isEven;
  }
  
  const isValid = sum % 10 === 0;
  
  return {
    isValid,
    error: isValid ? null : ERROR_MESSAGES.INVALID_CREDIT_CARD,
    cardType
  };
};

/**
 * Validate CVV
 * @param {string} cvv - CVV to validate
 * @param {string} cardType - Card type (optional)
 * @returns {object} Validation result
 */
export const validateCVV = (cvv, cardType = null) => {
  if (!cvv) {
    return { isValid: false, error: ERROR_MESSAGES.REQUIRED_FIELD };
  }
  
  const cleaned = cvv.replace(/\D/g, '');
  
  // American Express has 4-digit CVV, others have 3
  const expectedLength = cardType === 'amex' ? 4 : 3;
  const isValid = cleaned.length === expectedLength;
  
  return {
    isValid,
    error: isValid ? null : `CVV must be ${expectedLength} digits`
  };
};

/**
 * Validate expiry date
 * @param {string} month - Expiry month
 * @param {string} year - Expiry year
 * @returns {object} Validation result
 */
export const validateExpiryDate = (month, year) => {
  if (!month || !year) {
    return { isValid: false, error: ERROR_MESSAGES.REQUIRED_FIELD };
  }
  
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();
  
  const expMonth = parseInt(month, 10);
  const expYear = parseInt(year, 10);
  
  // Validate month range
  if (expMonth < 1 || expMonth > 12) {
    return { isValid: false, error: 'Invalid month' };
  }
  
  // Convert 2-digit year to 4-digit
  const fullYear = expYear < 100 ? 2000 + expYear : expYear;
  
  // Check if card is expired
  if (fullYear < currentYear || (fullYear === currentYear && expMonth < currentMonth)) {
    return { isValid: false, error: 'Card has expired' };
  }
  
  // Check if expiry is too far in the future (more than 20 years)
  if (fullYear > currentYear + 20) {
    return { isValid: false, error: 'Invalid expiry year' };
  }
  
  return { isValid: true, error: null };
};

/**
 * Enhanced number validation
 * @param {any} value - Value to validate
 * @param {object} options - Validation options
 * @returns {object} Validation result
 */
export const validateNumber = (value, options = {}) => {
  if (value === null || value === undefined || value === '') {
    return { isValid: false, error: ERROR_MESSAGES.REQUIRED_FIELD };
  }
  
  const num = Number(value);
  
  if (isNaN(num)) {
    return { isValid: false, error: 'Must be a valid number' };
  }
  
  if (options.min !== undefined && num < options.min) {
    return { isValid: false, error: `Must be at least ${options.min}` };
  }
  
  if (options.max !== undefined && num > options.max) {
    return { isValid: false, error: `Must be at most ${options.max}` };
  }
  
  if (options.integer && !Number.isInteger(num)) {
    return { isValid: false, error: 'Must be a whole number' };
  }
  
  if (options.positive && num <= 0) {
    return { isValid: false, error: 'Must be a positive number' };
  }
  
  if (options.nonNegative && num < 0) {
    return { isValid: false, error: 'Cannot be negative' };
  }
  
  return { isValid: true, error: null };
};

/**
 * Validate URL
 * @param {string} url - URL to validate
 * @param {object} options - Validation options
 * @returns {object} Validation result
 */
export const validateURL = (url, options = {}) => {
  if (!url) {
    return { isValid: false, error: ERROR_MESSAGES.REQUIRED_FIELD };
  }
  
  try {
    const urlObj = new URL(url);
    
    // Check protocol if specified
    if (options.protocols && !options.protocols.includes(urlObj.protocol.slice(0, -1))) {
      return { isValid: false, error: `URL must use one of: ${options.protocols.join(', ')}` };
    }
    
    // Check domain if specified
    if (options.allowedDomains && !options.allowedDomains.some(domain => urlObj.hostname.endsWith(domain))) {
      return { isValid: false, error: 'URL domain not allowed' };
    }
    
    return { isValid: true, error: null };
  } catch (error) {
    return { isValid: false, error: ERROR_MESSAGES.INVALID_URL };
  }
};

/**
 * Validate IP address
 * @param {string} ip - IP address to validate
 * @param {string} version - IP version ('v4', 'v6', or 'any')
 * @returns {object} Validation result
 */
export const validateIP = (ip, version = 'any') => {
  if (!ip) {
    return { isValid: false, error: ERROR_MESSAGES.REQUIRED_FIELD };
  }
  
  const ipv4Valid = VALIDATION_RULES.IPV4_REGEX.test(ip);
  const ipv6Valid = VALIDATION_RULES.IPV6_REGEX.test(ip);
  
  let isValid = false;
  
  switch (version) {
    case 'v4':
      isValid = ipv4Valid;
      break;
    case 'v6':
      isValid = ipv6Valid;
      break;
    case 'any':
    default:
      isValid = ipv4Valid || ipv6Valid;
      break;
  }
  
  return {
    isValid,
    error: isValid ? null : ERROR_MESSAGES.INVALID_IP,
    version: ipv4Valid ? 'v4' : ipv6Valid ? 'v6' : null
  };
};

/**
 * Enhanced file validation
 * @param {File} file - File to validate
 * @param {object} options - Validation options
 * @returns {object} Validation result
 */
export const validateFile = (file, options = {}) => {
  if (!file) {
    return { isValid: false, error: ERROR_MESSAGES.REQUIRED_FIELD };
  }
  
  // Check file size
  if (options.maxSize && file.size > options.maxSize) {
    const maxSizeMB = (options.maxSize / (1024 * 1024)).toFixed(2);
    return { isValid: false, error: `File size must be less than ${maxSizeMB}MB` };
  }
  
  if (options.minSize && file.size < options.minSize) {
    const minSizeKB = (options.minSize / 1024).toFixed(2);
    return { isValid: false, error: `File size must be at least ${minSizeKB}KB` };
  }
  
  // Check file type
  if (options.allowedTypes && !options.allowedTypes.includes(file.type)) {
    return { isValid: false, error: `File type ${file.type} is not allowed` };
  }
  
  // Check file extension
  if (options.allowedExtensions) {
    const extension = file.name.split('.').pop().toLowerCase();
    if (!options.allowedExtensions.includes(extension)) {
      return { isValid: false, error: `File extension .${extension} is not allowed` };
    }
  }
  
  // Check filename
  if (options.maxFilenameLength && file.name.length > options.maxFilenameLength) {
    return { isValid: false, error: `Filename is too long (max ${options.maxFilenameLength} characters)` };
  }
  
  return { isValid: true, error: null };
};

/**
 * Validate form data using rules
 * @param {object} data - Form data
 * @param {object} rules - Validation rules
 * @returns {object} Validation results
 */
export const validateForm = (data, rules) => {
  const errors = {};
  let isValid = true;
  
  Object.entries(rules).forEach(([field, fieldRules]) => {
    const value = data[field];
    
    // Check each rule for the field
    for (const rule of fieldRules) {
      let result;
      
      switch (rule.type) {
        case 'required':
          result = validateRequired(value, rule.message || field);
          break;
          
        case 'email':
          result = validateEmail(value);
          break;
          
        case 'phone':
          result = validatePhone(value, rule.options);
          break;
          
        case 'password':
          result = validatePassword(value, rule.options);
          break;
          
        case 'name':
          result = validateName(value, rule.options);
          break;
          
        case 'username':
          result = validateUsername(value);
          break;
          
        case 'cnic':
          result = validateCNIC(value);
          break;
          
        case 'number':
          result = validateNumber(value, rule.options);
          break;
          
        case 'date':
          result = validateDate(value, rule.options);
          break;
          
        case 'url':
          result = validateURL(value, rule.options);
          break;
          
        case 'ip':
          result = validateIP(value, rule.options?.version);
          break;
          
        case 'file':
          result = validateFile(value, rule.options);
          break;
          
        case 'creditCard':
          result = validateCreditCard(value);
          break;
          
        case 'cvv':
          result = validateCVV(value, rule.options?.cardType);
          break;
          
        case 'custom':
          result = rule.validator(value, data);
          break;
          
        default:
          result = { isValid: true, error: null };
      }
      
      if (!result.isValid) {
        errors[field] = result.error;
        isValid = false;
        break; // Stop checking other rules for this field
      }
    }
  });
  
  return { isValid, errors };
};

/**
 * Create custom validator
 * @param {Function} validatorFn - Validator function
 * @param {string} errorMessage - Error message
 * @returns {Function} Validator
 */
export const createValidator = (validatorFn, errorMessage) => {
  return (value) => {
    const isValid = validatorFn(value);
    return {
      isValid,
      error: isValid ? null : errorMessage
    };
  };
};

/**
 * Validate multiple fields with same rule
 * @param {object} data - Data object
 * @param {array} fields - Field names
 * @param {Function} validator - Validator function
 * @returns {object} Validation results
 */
export const validateMultipleFields = (data, fields, validator) => {
  const errors = {};
  let isValid = true;
  
  fields.forEach(field => {
    const result = validator(data[field]);
    if (!result.isValid) {
      errors[field] = result.error;
      isValid = false;
    }
  });
  
  return { isValid, errors };
};

// =============================================
// FORMATTERS
// =============================================

/**
 * Resolve the active currency ISO code from a `publicSettings` object.
 * Reads `payment.currency` first, then `menu.currency_symbol` (legacy),
 * then falls back to `CURRENCY.DEFAULT`. Use this anywhere outside React.
 * @param {object} publicSettings - The publicSettings slice from AppContext
 * @returns {string} ISO currency code (e.g. "PKR", "USD")
 */
export const getCurrencyCode = (publicSettings) => {
  const p = publicSettings || {};
  return (
    (p.payment && p.payment.currency) ||
    (p.menu && p.menu.currency_symbol) ||
    CURRENCY.DEFAULT
  );
};

/**
 * Format a monetary amount as "PKR 3,450.00" — ISO code + 2 decimals.
 * The currency argument is REQUIRED in practice; it should come from the
 * loaded public Settings. If omitted, falls back to `CURRENCY.DEFAULT`
 * so callers that run before settings load don't crash, but the result
 * may temporarily show the default code until settings hydrate.
 *
 * @param {number} amount - The amount to format
 * @param {string} [currency] - ISO currency code (default: CURRENCY.DEFAULT)
 * @param {string} [locale] - BCP-47 locale (default: en-US)
 * @returns {string} Formatted currency string, e.g. "PKR 3,450.00"
 */
export const formatCurrency = (amount, currency = CURRENCY.DEFAULT, locale = 'en-US') => {
  const code = (currency || CURRENCY.DEFAULT).toString().toUpperCase();
  const n = Number(amount);
  const safe = Number.isFinite(n) ? n : 0;

  try {
    // currencyDisplay: 'code' produces "PKR 3,450.00" instead of a symbol.
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      currencyDisplay: 'code',
      minimumFractionDigits: CURRENCY.DECIMAL_PLACES,
      maximumFractionDigits: CURRENCY.DECIMAL_PLACES,
    }).format(safe);
  } catch (_err) {
    // Unknown ISO codes throw — fall back to a manual format so the UI
    // still renders something sensible.
    const formatted = new Intl.NumberFormat(locale, {
      style: 'decimal',
      minimumFractionDigits: CURRENCY.DECIMAL_PLACES,
      maximumFractionDigits: CURRENCY.DECIMAL_PLACES,
    }).format(safe);
    return `${code} ${formatted}`;
  }
};

/**
 * Convenience: format using a `publicSettings` object directly.
 * Equivalent to `formatCurrency(amount, getCurrencyCode(publicSettings))`.
 * Useful for non-React utilities (PDFs, exports, etc.) that already have
 * the settings in hand.
 * @param {number} amount
 * @param {object} publicSettings
 * @param {string} [locale]
 * @returns {string}
 */
export const formatCurrencyFromSettings = (amount, publicSettings, locale = 'en-US') => {
  return formatCurrency(amount, getCurrencyCode(publicSettings), locale);
};

/**
 * Format date using various formats
 * @param {Date|string} date - Date to format
 * @param {string} format - Format type from DATE_FORMATS
 * @returns {string} Formatted date string
 */
export const formatDate = (date, format = DATE_FORMATS.DISPLAY) => {
  if (!date) return '';
  
  const dateObj = date instanceof Date ? date : new Date(date);
  
  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date';
  }
  
  // Simple format implementation - in production, use date-fns or moment.js
  const pad = (num) => String(num).padStart(2, '0');
  
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const fullMonths = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  
  const year = dateObj.getFullYear();
  const month = dateObj.getMonth();
  const day = dateObj.getDate();
  const hours = dateObj.getHours();
  const minutes = dateObj.getMinutes();
  
  switch (format) {
    case DATE_FORMATS.DISPLAY:
      return `${months[month]} ${pad(day)}, ${year}`;
      
    case DATE_FORMATS.DISPLAY_WITH_TIME:
      return `${months[month]} ${pad(day)}, ${year} ${pad(hours)}:${pad(minutes)}`;
      
    case DATE_FORMATS.INPUT:
      return `${year}-${pad(month + 1)}-${pad(day)}`;
      
    case DATE_FORMATS.SHORT:
      return `${pad(month + 1)}/${pad(day)}/${String(year).slice(-2)}`;
      
    case DATE_FORMATS.LONG:
      return `${fullMonths[month]} ${day}, ${year}`;
      
    case DATE_FORMATS.TIME_ONLY:
      return `${pad(hours)}:${pad(minutes)}`;
      
    case DATE_FORMATS.TIME_12H:
      const hour12 = hours % 12 || 12;
      const ampm = hours < 12 ? 'AM' : 'PM';
      return `${pad(hour12)}:${pad(minutes)} ${ampm}`;
      
    default:
      return dateObj.toISOString();
  }
};

/**
 * Format phone number
 * @param {string} phone - Phone number to format
 * @param {string} format - Format type ('US', 'INTL', 'PK')
 * @returns {string} Formatted phone number
 */
export const formatPhoneNumber = (phone, format = 'US') => {
  if (!phone) return '';
  
  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');
  
  if (format === 'US') {
    // Format: (123) 456-7890
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
  } else if (format === 'INTL') {
    // Format: +1 234 567 8900
    if (cleaned.length >= 10) {
      const countryCode = cleaned.slice(0, -10);
      const areaCode = cleaned.slice(-10, -7);
      const firstPart = cleaned.slice(-7, -4);
      const lastPart = cleaned.slice(-4);
      return `+${countryCode} ${areaCode} ${firstPart} ${lastPart}`;
    }
  } else if (format === 'PK') {
    // Format Pakistani numbers
    if (cleaned.length === 11 && cleaned.startsWith('0')) {
      return `${cleaned.slice(0, 4)}-${cleaned.slice(4, 11)}`;
    }
    if (cleaned.length === 12 && cleaned.startsWith('92')) {
      return `+92-${cleaned.slice(2, 5)}-${cleaned.slice(5)}`;
    }
  }
  
  return phone;
};

/**
 * Format credit card number with spaces
 * @param {string} cardNumber - Card number to format
 * @returns {string} Formatted card number
 */
export const formatCreditCard = (cardNumber) => {
  if (!cardNumber) return '';
  
  // Remove all non-numeric characters
  const cleaned = cardNumber.replace(/\D/g, '');
  
  // Add space every 4 digits
  const formatted = cleaned.match(/.{1,4}/g)?.join(' ') || cleaned;
  
  return formatted;
};

/**
 * Mask credit card number
 * @param {string} cardNumber - Card number to mask
 * @returns {string} Masked card number
 */
export const maskCreditCard = (cardNumber) => {
  if (!cardNumber) return '';
  
  const cleaned = cardNumber.replace(/\D/g, '');
  
  if (cleaned.length < 4) return cleaned;
  
  const last4 = cleaned.slice(-4);
  const masked = '*'.repeat(cleaned.length - 4) + last4;
  
  return formatCreditCard(masked);
};

/**
 * Format CNIC for display
 * @param {string} cnic - CNIC to format
 * @returns {string} Formatted CNIC
 */
export const formatCNIC = (cnic) => {
  if (!cnic) return '';
  
  const cleaned = cnic.replace(/\D/g, '');
  
  if (cleaned.length === 13) {
    return `${cleaned.slice(0, 5)}-${cleaned.slice(5, 12)}-${cleaned.slice(12)}`;
  }
  
  return cnic;
};

/**
 * Format file size
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Format percentage
 * @param {number} value - Value to format as percentage
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted percentage
 */
export const formatPercentage = (value, decimals = 0) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%';
  }
  
  return `${(value * 100).toFixed(decimals)}%`;
};

/**
 * Format number with commas
 * @param {number} num - Number to format
 * @returns {string} Formatted number
 */
export const formatNumber = (num) => {
  if (num === null || num === undefined || isNaN(num)) {
    return '0';
  }
  
  return num.toLocaleString('en-US');
};

/**
 * Format duration (in minutes) to human readable format
 * @param {number} minutes - Duration in minutes
 * @returns {string} Formatted duration
 */
export const formatDuration = (minutes) => {
  if (!minutes || minutes < 0) return '0 min';
  
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  
  const parts = [];
  
  if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`);
  if (hours > 0) parts.push(`${hours} hour${hours > 1 ? 's' : ''}`);
  if (mins > 0) parts.push(`${mins} min`);
  
  return parts.join(' ') || '0 min';
};

/**
 * Format name (capitalize first letter of each word)
 * @param {string} name - Name to format
 * @returns {string} Formatted name
 */
export const formatName = (name) => {
  if (!name) return '';
  
  return name
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Format email (lowercase)
 * @param {string} email - Email to format
 * @returns {string} Formatted email
 */
export const formatEmail = (email) => {
  if (!email) return '';
  
  return email.trim().toLowerCase();
};

/**
 * Format reservation status
 * @param {string} status - Status to format
 * @returns {string} Formatted status with appropriate color class
 */
export const formatReservationStatus = (status) => {
  const statusMap = {
    pending: { label: 'Pending', class: 'text-yellow-600 bg-yellow-100' },
    confirmed: { label: 'Confirmed', class: 'text-green-600 bg-green-100' },
    checked_in: { label: 'Checked In', class: 'text-blue-600 bg-blue-100' },
    checked_out: { label: 'Checked Out', class: 'text-gray-600 bg-gray-100' },
    cancelled: { label: 'Cancelled', class: 'text-red-600 bg-red-100' },
    no_show: { label: 'No Show', class: 'text-orange-600 bg-orange-100' },
  };
  
  return statusMap[status] || { label: status, class: 'text-gray-600 bg-gray-100' };
};

/**
 * Format address
 * @param {object} address - Address object
 * @returns {string} Formatted address
 */
export const formatAddress = (address) => {
  if (!address) return '';
  
  const parts = [
    address.street1,
    address.street2,
    address.city,
    address.state,
    address.zipCode,
    address.country,
  ].filter(Boolean);
  
  return parts.join(', ');
};

/**
 * Format time ago (e.g., "2 hours ago")
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted relative time
 */
export const formatTimeAgo = (date) => {
  if (!date) return '';
  
  const dateObj = date instanceof Date ? date : new Date(date);
  const now = new Date();
  const diffInSeconds = Math.floor((now - dateObj) / 1000);
  
  if (diffInSeconds < 60) {
    return 'just now';
  }
  
  const intervals = [
    { label: 'year', seconds: 31536000 },
    { label: 'month', seconds: 2592000 },
    { label: 'day', seconds: 86400 },
    { label: 'hour', seconds: 3600 },
    { label: 'minute', seconds: 60 },
  ];
  
  for (const interval of intervals) {
    const count = Math.floor(diffInSeconds / interval.seconds);
    if (count >= 1) {
      return `${count} ${interval.label}${count > 1 ? 's' : ''} ago`;
    }
  }
  
  return 'just now';
};

/**
 * Format room type
 * @param {string} type - Room type
 * @returns {string} Formatted room type
 */
export const formatRoomType = (type) => {
  if (!type) return '';
  
  return type
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Format nights count
 * @param {Date|string} checkIn - Check-in date
 * @param {Date|string} checkOut - Check-out date
 * @returns {string} Formatted nights count
 */
export const formatNights = (checkIn, checkOut) => {
  if (!checkIn || !checkOut) return '0 nights';
  
  const checkInDate = checkIn instanceof Date ? checkIn : new Date(checkIn);
  const checkOutDate = checkOut instanceof Date ? checkOut : new Date(checkOut);
  
  const diffInMs = checkOutDate - checkInDate;
  const nights = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
  
  if (nights <= 0) return '0 nights';
  
  return `${nights} night${nights > 1 ? 's' : ''}`;
};

/**
 * Truncate text with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text;
  
  return text.substring(0, maxLength).trim() + '...';
};

// Export all validators in a namespace
export const validators = {
  validateRequired,
  validateEmail,
  validatePhone,
  validatePassword,
  validatePasswordConfirmation,
  validateName,
  validateUsername,
  validateCNIC,
  validateDate,
  validateDateRange,
  validateCreditCard,
  validateCVV,
  validateExpiryDate,
  validateNumber,
  validateURL,
  validateIP,
  validateFile,
  validateForm,
  validateMultipleFields,
  createValidator,
  getPasswordStrength,
};

// Export all formatters in a namespace
export const formatters = {
  formatCurrency,
  formatDate,
  formatPhoneNumber,
  formatCreditCard,
  maskCreditCard,
  formatCNIC,
  formatFileSize,
  formatPercentage,
  formatNumber,
  formatDuration,
  formatName,
  formatEmail,
  formatReservationStatus,
  formatAddress,
  formatTimeAgo,
  formatRoomType,
  formatNights,
  truncateText,
};

// Export all constants in a namespace
export const constants = {
  API_BASE_URL,
  API_ENDPOINTS,
  ROOM_TYPES,
  ROOM_TYPE_LABELS,
  RESERVATION_STATUS,
  RESERVATION_STATUS_LABELS,
  PAYMENT_STATUS,
  PAYMENT_METHODS,
  USER_ROLES,
  DATE_FORMATS,
  CURRENCY,
  PAGINATION,
  VALIDATION_RULES,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  STORAGE_KEYS,
  THEME,
  LANGUAGES,
  SOCIAL_LINKS,
  CONTACT_INFO,
  MAP_CONFIG,
  FILE_UPLOAD,
  ANIMATION_DURATION,
  BREAKPOINTS,
  Z_INDEX,
};

// Default export with everything
export default {
  ...constants,
  ...validators,
  ...formatters,
};